import IFrontDriver from '../../NativeDevice/Front/IFrontDriver';
import { SagaIterator } from 'redux-saga';
import ApplicationType from '@signageos/common-types/dist/Application/ApplicationType';
import { IAppletTimingDefinition, UpdateActiveAppletBinary } from '@signageos/actions/dist/Applet/appletActions';
import { SUPRA_APPLET_UID } from './supraConstants';
import { call } from 'redux-saga/effects';
import { IPropertyStorage } from '../../Property/propertyStorage';
import {
	getAvailableSupraServer,
	getSupraAppletChecksum,
	getSupraCoreAppConfigFromServerConfig,
	getSupraCoreAppDefaultConfig,
	getSupraScreenCredentials,
	IServerSupraScreenConfig,
	parseSupraCoreAppConfigFromDSN,
	SupraServer,
} from './supraConfig';
import Property from '../../Property/Property';
import FrontCapability from '../../NativeDevice/Front/FrontCapability';

export function* getSupraUpdateActiveAppletBinary(
	frontDriver: IFrontDriver,
	propertyStorage: IPropertyStorage,
	definition: IAppletTimingDefinition,
	authHash: string | null,
): SagaIterator<UpdateActiveAppletBinary | null> {
	if (!(yield call([frontDriver, frontDriver.frontSupports], FrontCapability.SUPRA))) {
		// display-supra Core App has to not report FrontCapability.SUPRA support to prevent infinite mirroring loop where display-supra would try to launch itself on top of itself.
		// If Supra capability is not supported, return null to not launch the applet.
		return null;
	}
	if (definition.configuration.supra === 'enabled') {
		const deviceUid: string = yield call([frontDriver, frontDriver.getDeviceUid]);
		const applicationType: ApplicationType = yield call([frontDriver, frontDriver.getApplicationType]);
		if (!authHash) {
			console.warn('Cannot show Supra applet - authentication hash is missing');
			return null;
		}
		const supraScreenServerConfig: IServerSupraScreenConfig | null = yield call(
			propertyStorage.getValueOrDefault,
			Property.SUPRA_SCREEN_CONFIG,
			null,
		);
		if (!supraScreenServerConfig) {
			console.info('Supra screen config not yet available in propertyStorage, falling back to empty server list');
		}
		const supraScreenAppletCredentials = getSupraScreenCredentials(deviceUid, authHash);
		const supraServer: SupraServer = yield call(getAvailableSupraServer, supraScreenServerConfig?.servers || []);
		const supraCoreAppDefaultConfig = getSupraCoreAppDefaultConfig();
		const supraCoreAppConfig = getSupraCoreAppConfigFromServerConfig(supraServer);
		const supraCoreAppOverrideConfig = parseSupraCoreAppConfigFromDSN(definition.configuration.supraDsn);
		const secure = supraCoreAppOverrideConfig?.secure ?? supraCoreAppConfig?.secure ?? supraCoreAppDefaultConfig.secure;
		const supraScreenAppletVersion =
			supraCoreAppOverrideConfig?.supraScreenAppletVersion ??
			supraCoreAppConfig?.supraScreenAppletVersion ??
			supraCoreAppDefaultConfig.supraScreenAppletVersion;
		if (!supraCoreAppConfig) {
			console.warn('Cannot show Supra applet - Supra server configuration is missing');
			return null;
		}
		if (!supraCoreAppConfig.hostname) {
			console.warn('Cannot show Supra applet - Supra server hostname is missing');
			return null;
		}
		const appArgs = ['--', '--device-uid', deviceUid, '--application-type', applicationType];
		const configuration = {
			...supraScreenAppletCredentials,
			...supraCoreAppConfig,
			hostname: supraCoreAppConfig.hostname,
			appName: supraCoreAppConfig.appName,
			extraArgs: appArgs.join(' '),
			// supraDsn can optionally overrides server config if provided. Usually for debugging purposes
			...supraCoreAppOverrideConfig,
			secure: secure ? 1 : 0,
		};
		const action: UpdateActiveAppletBinary = {
			type: UpdateActiveAppletBinary,
			activeAppletUid: SUPRA_APPLET_UID,
			activeConfiguration: configuration,
			activeTimingChecksum: getSupraAppletChecksum(supraScreenAppletVersion, configuration),
			activeAppletVersion: supraScreenAppletVersion,
			activeAppletVersionPostfix: undefined,
			activeAppletFrontAppletVersion: undefined,
			activeAppletIsPackage: true,
			activeAppletMetadata: undefined,
		};
		return action;
	}
	return null;
}
